// DiaryResultsPage — the legacy app's most-used screen.
//
// Reached via /diary/results?d=<encryptedParams>. The single ?d= value
// decrypts to { driver_id, week_start } via src/utils/urlParams. Tampered
// or missing params toast + redirect to /diary.
//
// Layout (post-correction):
//   - PageHeader: "Diary — {driver}", week label, prev/next arrows, driver
//     switcher (cached drivers list shared with /diary).
//   - DiaryTabBar (Mon–Sun) below the header. One day visible at a time.
//   - Active day section with its own DndContext (cross-day drops blocked
//     at the library level — there is only one DndContext mounted at a
//     time so there is nothing to drop into outside the active day).
//   - SaveOrderBar fixed bottom when any day has unsaved reorder edits.
//   - NotesModal on demand.
//   - ConfirmModal via React Router useBlocker for unsaved nav.

import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Link,
  useBlocker,
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import {
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  ArrowLeft,
  AlertTriangle,
} from 'lucide-react';
import { PageHeader } from '../../components/ui/PageHeader/PageHeader';
import { Button } from '../../components/ui/Button/Button';
import { Spinner } from '../../components/ui/Spinner/Spinner';
import { ConfirmModal } from '../../components/ui/ConfirmModal/ConfirmModal';
import { PrintTemplateActions } from '../../components/ui/PrintTemplateActions/PrintTemplateActions';
import { Select } from '../../components/form/Select';
import { useToast } from '../../hooks/useToast';
import { useAuthStore } from '../../store/authStore';
import { useDriverHolidayCheck } from '../../hooks/useDriverHolidayCheck';
import {
  effectivePeriodOnDate,
  holidaysCoveringDate,
  periodLabel,
} from '../../lib/holidayPeriod';
import { queryKeys } from '../../api/queryKeys';
import {
  decryptParams,
  encryptParams,
  UrlParamError,
} from '../../utils/urlParams';
import { useDiaryDrivers } from './hooks/useDiaryDrivers';
import { useDiaryResults } from './hooks/useDiaryResults';
import { useDiaryReorder } from './hooks/useDiaryReorder';
import { useWeekNotes } from './hooks/useDayNotes';
import { mondayOfISO } from './DiaryPage';
import { DiaryDaySection } from './components/DiaryDaySection';
import { DiaryTabBar, type DiaryTab } from './components/DiaryTabBar';
import { SaveOrderBar } from './components/SaveOrderBar';
import { NotesModal } from './components/NotesModal';
import { DayNotesSection } from './components/DayNotesSection';
import type { BookingSummary, DiaryWeeklyResponse } from '../../types/api';
import styles from './DiaryResultsPage.module.css';

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 24 * 60 * 60 * 1000;
const WEEK_MS = 7 * DAY_MS;

/**
 * Active-tab day-name vocabulary. Order matches Mon→Sun (`offset` 0..6
 * relative to `week_start`). The active tab persists in the URL as
 * `?tab=<name>` so note mutations can refetch the diary week without
 * resetting the user's current day view.
 */
const DAY_NAMES = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
type DayName = (typeof DAY_NAMES)[number];

function parseISO(s: string): Date | null {
  if (!ISO_DATE_RE.test(s)) return null;
  const [y, m, d] = s.split('-').map(Number);
  const dt = new Date(y ?? 1970, (m ?? 1) - 1, d ?? 1);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function isoDate(d: Date): string {
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

function shiftWeek(iso: string, weeks: number): string {
  const dt = parseISO(iso);
  if (!dt) return iso;
  return isoDate(new Date(dt.getTime() + weeks * WEEK_MS));
}

function formatRange(start: string, end: string): string {
  const s = parseISO(start);
  const e = parseISO(end);
  if (!s || !e) return `${start} – ${end}`;
  const fmt = new Intl.DateTimeFormat(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return `${fmt.format(s)} – ${fmt.format(e)}`;
}

function todayISO(): string {
  return isoDate(new Date());
}

/** Filename-safe slug — strips anything that isn't alphanumeric /
 *  underscore / dash, and clamps the length so the eventual
 *  Content-Disposition stays well under most OS path limits. */
function slugify(s: string): string {
  return s.replace(/[^a-zA-Z0-9_-]+/g, '_').slice(0, 60);
}

interface DecryptedParams {
  driverId: number;
  weekStart: string;
}

function isWithinWeek(date: string, weekStart: string): boolean {
  const d = parseISO(date);
  const ws = parseISO(weekStart);
  if (!d || !ws) return false;
  const ms = d.getTime() - ws.getTime();
  return ms >= 0 && ms < WEEK_MS;
}

export function DiaryResultsPage(): JSX.Element {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const toast = useToast();
  const queryClient = useQueryClient();
  const urlParamSecret = useAuthStore((s) => s.urlParamSecret);
  const authStatus = useAuthStore((s) => s.status);
  // `generated_by` for the print template render context — the staff
  // member triggering the print. Falls back to an empty string when
  // the auth store has no user (the page already gates on
  // authenticated state above, so this is mostly belt-and-braces).
  const currentUserName = useAuthStore((s) => s.user?.name ?? '');

  const rawD = searchParams.get('d');

  // -------- decrypt ?d= -------------------------------------------------
  const [decrypted, setDecrypted] = useState<DecryptedParams | null>(null);
  const [decryptFailed, setDecryptFailed] = useState(false);
  const [decryptInFlight, setDecryptInFlight] = useState(false);

  useEffect(() => {
    if (!rawD || authStatus !== 'authenticated' || !urlParamSecret) return;
    let cancelled = false;
    setDecryptInFlight(true);
    (async () => {
      try {
        const params = await decryptParams(rawD, urlParamSecret);
        const driverId = Number(params.driver_id);
        const weekStartRaw = String(params.week_start ?? '');
        if (
          !Number.isInteger(driverId) ||
          driverId <= 0 ||
          !ISO_DATE_RE.test(weekStartRaw)
        ) {
          throw new UrlParamError();
        }
        const weekStart = mondayOfISO(weekStartRaw);
        if (cancelled) return;
        setDecrypted({ driverId, weekStart });
        setDecryptFailed(false);
      } catch {
        if (cancelled) return;
        setDecryptFailed(true);
      } finally {
        if (!cancelled) setDecryptInFlight(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rawD, urlParamSecret, authStatus]);

  // Redirect on missing/invalid params.
  useEffect(() => {
    if (authStatus !== 'authenticated') return;
    const missing = !rawD;
    if (missing || decryptFailed) {
      toast.error('Invalid diary link — please select a driver and week.');
      navigate('/collections/diary', { replace: true });
    }
  }, [authStatus, rawD, decryptFailed, toast, navigate]);

  const driverId = decrypted?.driverId ?? 0;
  const weekStart = decrypted?.weekStart ?? '0000-00-00';

  // -------- data fetch --------------------------------------------------
  const driversQuery = useDiaryDrivers();
  const diaryQuery = useDiaryResults({
    driverId,
    weekStart,
    pauseRefetch: false,
  });

  const reorder = useDiaryReorder({
    driverId,
    weekStart,
    serverDays: diaryQuery.data?.days,
  });

  // Notes come from the dedicated /diary/notes endpoint, NOT from the
  // weekly response's embedded `days[].notes` arrays — those are unreliable
  // due to a known server bug (HANDOFF gap §11). The hook gates itself on
  // valid driver_id + week_start so it doesn't fire with placeholders.
  const weekNotesQuery = useWeekNotes({ driverId, weekStart });

  // Holiday probe — keyed on driver uuid + week range so day-tab styling and
  // the per-day banner can react instantly when the user navigates weeks.
  const driverUuid = driversQuery.data?.find((d) => d.id === driverId)?.uuid ?? null;
  const weekEndForCheck = (() => {
    if (weekStart === '0000-00-00') return null;
    const dt = parseISO(weekStart);
    if (!dt) return null;
    return isoDate(new Date(dt.getTime() + 6 * DAY_MS));
  })();
  const holidayCheck = useDriverHolidayCheck(driverUuid, weekStart, weekEndForCheck);

  function isHolidayDate(date: string): boolean {
    return holidayCheck.holidays.some(
      (h) => date >= h.start_date && date <= h.end_date,
    );
  }

  // Pause auto-refresh while user has unsaved local edits.
  useEffect(() => {
    if (!reorder.hasUnsavedChanges || !decrypted) return;
    void queryClient.cancelQueries({
      queryKey: queryKeys.diary.week(driverId, weekStart),
    });
  }, [reorder.hasUnsavedChanges, decrypted, queryClient, driverId, weekStart]);

  // -------- active tab (URL-search-param-backed) ------------------------
  // Stored as `&tab=monday|tuesday|…|sunday` alongside the encrypted `?d=`.
  // URL-backed so that:
  //   1. note mutations that invalidate the diary week query don't reset
  //      the tab (the param is unchanged across the rerender);
  //   2. the tab survives a page refresh and is shareable;
  //   3. week navigation can clear the tab via the same setSearchParams
  //      call that updates `?d=`, restoring the default for the new week.
  const tabParam = (searchParams.get('tab') ?? '').toLowerCase();
  const tabOffset = DAY_NAMES.indexOf(tabParam as DayName);

  const computeDefaultActive = useCallback(
    (week: DiaryWeeklyResponse): string => {
      const today = todayISO();
      if (isWithinWeek(today, week.week_start)) {
        const match = week.days.find((d) => d.date === today);
        if (match) return match.date;
      }
      return week.days[0]?.date ?? week.week_start;
    },
    [],
  );

  // First-load tab seeding: when ?tab= is absent on entry, compute the
  // default and write it back so subsequent renders read the same value.
  // `replace: true` keeps the back button clean (no history entry for the
  // implicit default).
  useEffect(() => {
    if (!diaryQuery.data) return;
    if (tabOffset !== -1) return;
    const def = computeDefaultActive(diaryQuery.data);
    const defOffset = Math.floor(
      (parseISO(def)!.getTime() - parseISO(diaryQuery.data.week_start)!.getTime()) /
        DAY_MS,
    );
    const dayName = DAY_NAMES[Math.max(0, Math.min(6, defOffset))] ?? 'monday';
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', dayName);
    setSearchParams(sp, { replace: true });
  }, [diaryQuery.data, tabOffset, computeDefaultActive, searchParams, setSearchParams]);

  const activeDate: string | null = useMemo(() => {
    if (!diaryQuery.data) return null;
    if (tabOffset !== -1) {
      const day = diaryQuery.data.days[tabOffset];
      if (day) return day.date;
    }
    return computeDefaultActive(diaryQuery.data);
  }, [diaryQuery.data, tabOffset, computeDefaultActive]);

  function setActiveTabFromDate(date: string): void {
    const data = diaryQuery.data;
    if (!data) return;
    const offset = data.days.findIndex((d) => d.date === date);
    if (offset === -1) return;
    const dayName = DAY_NAMES[offset] ?? 'monday';
    const sp = new URLSearchParams(searchParams);
    sp.set('tab', dayName);
    setSearchParams(sp);
  }

  // -------- notes modal -------------------------------------------------
  const [editingBooking, setEditingBooking] = useState<BookingSummary | null>(null);
  const [notesModalOpen, setNotesModalOpen] = useState(false);

  function openNotesEditor(booking: BookingSummary) {
    setEditingBooking(booking);
    setNotesModalOpen(true);
  }

  function closeNotesEditor() {
    setNotesModalOpen(false);
    setTimeout(() => setEditingBooking(null), 150);
  }

  async function handleNotesSaved() {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.diary.week(driverId, weekStart),
    });
  }

  // -------- nav handlers (encrypt before navigating) --------------------
  // `keepTab` controls whether the existing `tab=` param survives the
  // navigation. Driver switch keeps the tab (the date axis is unchanged
  // — Tuesday for driver A is Tuesday for driver B). Week change drops
  // the tab so the new week recomputes its default (today/Monday).
  const navigateToParams = useCallback(
    async (
      nextDriverId: number,
      nextWeekStart: string,
      options: { keepTab: boolean },
    ): Promise<void> => {
      if (!urlParamSecret) {
        toast.error('Could not sign the URL — try refreshing.');
        return;
      }
      try {
        const encoded = await encryptParams(
          { driver_id: nextDriverId, week_start: nextWeekStart },
          urlParamSecret,
        );
        const sp = new URLSearchParams();
        sp.set('d', encoded);
        if (options.keepTab) {
          const existingTab = searchParams.get('tab');
          if (existingTab) sp.set('tab', existingTab);
        }
        setSearchParams(sp);
      } catch {
        toast.error('Could not encode the URL — try refreshing.');
      }
    },
    [urlParamSecret, searchParams, setSearchParams, toast],
  );

  function changeWeek(weeksDelta: number) {
    if (!decrypted) return;
    const next = shiftWeek(decrypted.weekStart, weeksDelta);
    void navigateToParams(decrypted.driverId, next, { keepTab: false });
  }

  function changeDriver(nextDriverId: string) {
    if (!decrypted) return;
    const id = Number(nextDriverId);
    if (!Number.isFinite(id)) return;
    void navigateToParams(id, decrypted.weekStart, { keepTab: true });
  }

  // -------- save order --------------------------------------------------
  async function handleSaveOrder() {
    try {
      await reorder.saveOrder();
      toast.success('Order saved');
    } catch (err) {
      const message = axios.isAxiosError(err)
        ? (err.response?.data as { message?: string } | undefined)?.message ??
          'Could not save order. Reverted to server order.'
        : 'Could not save order. Reverted to server order.';
      toast.error(message);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.diary.week(driverId, weekStart),
      });
      reorder.resetOrder();
    }
  }

  // -------- unsaved-changes guard ---------------------------------------
  // Tab clicks now navigate (the active tab lives in `?tab=`), so the
  // raw boolean form of useBlocker would prompt the user every time
  // they switch days. Use the function form to allow tab-only changes
  // (same `?d=`, different `?tab=`) and only block when `?d=` itself
  // changes (week / driver switch) or the user leaves the page entirely.
  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    if (!reorder.hasUnsavedChanges) return false;
    if (currentLocation.pathname !== nextLocation.pathname) return true;
    const cur = new URLSearchParams(currentLocation.search);
    const nxt = new URLSearchParams(nextLocation.search);
    return cur.get('d') !== nxt.get('d');
  });

  // -------- tabs --------------------------------------------------------
  const tabs: DiaryTab[] = useMemo(() => {
    if (!reorder.orderedDays.length) return [];
    const changedSet = new Set(reorder.changedDayDates);
    return reorder.orderedDays.map((d) => ({
      date: d.date,
      dayOfWeek: d.day_of_week,
      bookingCount: d.bookings.length,
      changed: changedSet.has(d.date),
      holiday: holidayCheck.holidays.some(
        (h) => d.date >= h.start_date && d.date <= h.end_date,
      ),
    }));
  }, [reorder.orderedDays, reorder.changedDayDates, holidayCheck.holidays]);

  const activeDay = useMemo(
    () =>
      activeDate
        ? reorder.orderedDays.find((d) => d.date === activeDate) ?? null
        : null,
    [reorder.orderedDays, activeDate],
  );

  // Effective holiday period (FULL / AM / PM) on the open day tab; drives the
  // banner wording and the per-booking clash flag in the day section.
  const activeDayHolidayPeriod = activeDay
    ? effectivePeriodOnDate(holidayCheck.holidays, activeDay.date)
    : null;

  // -------- print scope toggle ------------------------------------------
  // "Full Week" (default) sends the entire week.days array to the
  // renderer; "Active Day" sends a single-element array containing
  // just the day matching the current ?tab=. Disabled when no day
  // tab is selected (only happens during the very first render before
  // the default-tab effect lands).
  const [printScope, setPrintScope] = useState<'week' | 'day'>('week');
  // Auto-revert to "Full Week" if the active day disappears (e.g. the
  // user navigates to a week where the URL ?tab= no longer maps to a
  // valid day). Avoids sending `days: [undefined]` to the renderer.
  useEffect(() => {
    if (printScope === 'day' && activeDate === null) {
      setPrintScope('week');
    }
  }, [printScope, activeDate]);

  // -------- render ------------------------------------------------------
  if (
    authStatus !== 'authenticated' ||
    !rawD ||
    decryptFailed ||
    decryptInFlight ||
    !decrypted
  ) {
    if (decryptFailed || (!rawD && authStatus === 'authenticated')) {
      return <div />;
    }
    return (
      <div className={styles.shell}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Spinner label="Loading diary" />
        </div>
      </div>
    );
  }

  if (driversQuery.isLoading || diaryQuery.isLoading) {
    return (
      <div className={styles.shell}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
          <Spinner label="Loading diary" />
        </div>
      </div>
    );
  }

  if (diaryQuery.isError || !diaryQuery.data) {
    return (
      <div className={styles.shell}>
        <div className={styles.errorBanner} role="alert">
          Could not load the weekly diary. Try refreshing the page.
        </div>
        <Link to="/collections/diary" style={{ alignSelf: 'flex-start', textDecoration: 'none' }}>
          <Button variant="secondary" icon={ArrowLeft}>
            Back to selector
          </Button>
        </Link>
      </div>
    );
  }

  const week: DiaryWeeklyResponse = diaryQuery.data;
  const driverOptions = (driversQuery.data ?? []).map((d) => ({
    value: String(d.id),
    label: d.name,
  }));

  return (
    <div className={styles.shell}>
      <PageHeader
        title={`Diary — ${week.driver.name}`}
        subtitle={
          <span className={styles.summary}>
            Week of {formatRange(week.week_start, week.week_end)} · {week.totals.bookings}{' '}
            booking{week.totals.bookings === 1 ? '' : 's'} · £
            {week.totals.revenue.toFixed(2)}
          </span>
        }
        actions={
          <div className={styles.headerActions} data-print-hide>
            <div className={styles.weekNav}>
              <Button
                variant="secondary"
                icon={ChevronLeft}
                aria-label="Previous week"
                size="sm"
                onClick={() => changeWeek(-1)}
              />
              <span className={styles.weekLabel}>
                <CalendarDays size={14} aria-hidden />
                {formatRange(week.week_start, week.week_end)}
              </span>
              <Button
                variant="secondary"
                icon={ChevronRight}
                aria-label="Next week"
                size="sm"
                onClick={() => changeWeek(1)}
              />
            </div>
            <div className={styles.driverSwitcherWrap}>
              <Select
                aria-label="Driver"
                value={String(driverId)}
                onChange={(e) => changeDriver(e.target.value)}
                options={driverOptions}
              />
            </div>
            {/* Print scope: full week vs single active day. Sits
                immediately to the left of the Print button so the
                pairing is obvious. */}
            <div className={styles.printScopeWrap}>
              <Select
                aria-label="Print scope"
                value={printScope}
                onChange={(e) =>
                  setPrintScope(e.target.value === 'day' ? 'day' : 'week')
                }
                options={[
                  { value: 'week', label: 'Full Week' },
                  {
                    value: 'day',
                    label: 'Active Day',
                    disabled: activeDate === null,
                  },
                ]}
              />
            </div>
            {/* Print + Download PDF — backed by the per-tenant
                diary_results print template (Phase 8 of
                PRINT_TEMPLATE_PLAN.md). The legacy `data-print-hide`
                / `data-print-show` CSS below is kept as a fallback
                per DECISION-TPL-015. */}
            <PrintTemplateActions
              templateType="diary_results"
              buildContext={() => {
                const scopedDays =
                  printScope === 'day' && activeDate !== null
                    ? week.days.filter((d) => d.date === activeDate)
                    : week.days;
                return {
                  driver_name: week.driver.name,
                  week_start_date: week.week_start,
                  week_end_date: week.week_end,
                  total_bookings: week.totals.bookings,
                  total_revenue: week.totals.revenue,
                  days: scopedDays,
                  generated_by: currentUserName,
                };
              }}
              filename={
                printScope === 'day' && activeDate !== null
                  ? `diary_${slugify(week.driver.name)}_${activeDate}.pdf`
                  : `diary_${slugify(week.driver.name)}_${week.week_start}.pdf`
              }
            />
          </div>
        }
      />

      {/* Print-only header rendered above the data; hidden on screen.
          Single h1 with the week label appended so its text differs from
          the screen-only PageHeader h1 (avoids accessible-name collision
          in tests + screen readers). */}
      <div data-print-show>
        <h1 style={{ margin: 0 }}>
          Diary — {week.driver.name} — Week of{' '}
          {formatRange(week.week_start, week.week_end)}
        </h1>
        <p style={{ margin: '0.25rem 0 0.75rem' }}>
          {week.totals.bookings} booking{week.totals.bookings === 1 ? '' : 's'} · £
          {week.totals.revenue.toFixed(2)}
        </p>
      </div>

      <div data-print-hide>
        <DiaryTabBar
          tabs={tabs}
          activeDate={activeDate ?? week.week_start}
          onChange={setActiveTabFromDate}
        />
      </div>

      <div
        className={styles.activePanel}
        role="tabpanel"
        id={`diary-panel-${activeDate ?? week.week_start}`}
        aria-labelledby={`diary-tab-${activeDate ?? week.week_start}`}
      >
        {activeDay ? (
          <>
            {isHolidayDate(activeDay.date) ? (
              <div role="alert" className={styles.holidayBanner}>
                <AlertTriangle size={18} aria-hidden />
                <div className={styles.holidayBannerBody}>
                  <strong>
                    {activeDayHolidayPeriod === 'FULL' || activeDayHolidayPeriod === null
                      ? 'Driver on Holiday — Do not book jobs'
                      : `Driver on half-day holiday (${periodLabel(activeDayHolidayPeriod)}) — do not book ${activeDayHolidayPeriod} jobs`}
                  </strong>
                  <ul className={styles.holidayList}>
                    {holidaysCoveringDate(holidayCheck.holidays, activeDay.date).map((h) => (
                      <li key={`${h.start_date}-${h.end_date}-${h.period}`}>
                        {h.start_date} to {h.end_date}
                        {h.period !== 'FULL' ? ` (${periodLabel(h.period)})` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            ) : null}
            <DayNotesSection
              driverId={driverId}
              weekStart={weekStart}
              date={activeDay.date}
              dayOfWeek={activeDay.day_of_week}
              notes={(weekNotesQuery.data ?? []).filter((n) => n.date === activeDay.date)}
            />
            <div style={{ marginTop: '0.75rem' }} data-print-page-break="before">
              <DiaryDaySection
                day={activeDay}
                dayChanged={reorder.changedDayDates.includes(activeDay.date)}
                onMove={reorder.moveBooking}
                onEditNotes={openNotesEditor}
                diaryWeekKey={queryKeys.diary.week(driverId, weekStart)}
                holidayPeriod={activeDayHolidayPeriod}
              />
            </div>
          </>
        ) : null}
      </div>

      <div data-print-hide>
        <SaveOrderBar
          changedDays={reorder.changedDayNames}
          onSave={handleSaveOrder}
          onReset={reorder.resetOrder}
          saving={reorder.isSaving}
        />
      </div>

      <NotesModal
        open={notesModalOpen}
        booking={editingBooking}
        onClose={closeNotesEditor}
        onSaved={() => void handleNotesSaved()}
      />

      <ConfirmModal
        open={blocker.state === 'blocked'}
        title="Unsaved diary changes"
        message="You have unsaved diary changes. Leave anyway?"
        variant="danger"
        confirmLabel="Leave without saving"
        cancelLabel="Stay on page"
        onConfirm={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
    </div>
  );
}
